// The pure include/exclude selection model behind the shared filter menus
// (see components/filters/include_exclude_filter_menu.tsx for the UI).
// Lives in lib so non-UI code (query building, lane filtering) can depend
// on the model without pulling in components.

// Include/exclude selection for one category. Empty include means "no
// restriction"; exclude wins over include for the same value.
export type FilterMenuSelection = {
    readonly include: ReadonlySet<string>;
    readonly exclude: ReadonlySet<string>;
};

export const EMPTY_FILTER_SELECTION: FilterMenuSelection = {
    include: new Set<string>(),
    exclude: new Set<string>(),
};

/**
 * Toggles a value in one half of the selection; adding to include removes
 * it from exclude and vice versa, so a value is never in both sets.
 */
export function toggledFilterSelection(
    previous: FilterMenuSelection,
    value: string,
    mode: 'include' | 'exclude',
): FilterMenuSelection {
    const include = new Set(previous.include);
    const exclude = new Set(previous.exclude);
    const target = mode === 'include' ? include : exclude;
    const other = mode === 'include' ? exclude : include;
    if (target.has(value)) {
        target.delete(value);
    } else {
        target.add(value);
        other.delete(value);
    }
    return { include, exclude };
}

export function filterSelectionActive(selection: FilterMenuSelection): boolean {
    return selection.include.size > 0 || selection.exclude.size > 0;
}

export function matchesFilterSelection(
    value: string,
    selection: FilterMenuSelection,
): boolean {
    if (selection.exclude.has(value)) {
        return false;
    }
    return selection.include.size === 0 || selection.include.has(value);
}
